/**
 * WebSocket Client
 * 
 * Real-time communication client for Blue Clipboard WebSocket API
 * Matches legacy websocket-client.js functionality
 * - JWT token validation on connection
 * - Automatic reconnection with exponential backoff
 * - HTTP fallback when WebSocket unavailable
 * - Event-based message handling
 * - Connection state management
 * 
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7
 */

import { getStoredTokens } from '../auth/cognito';
import { restClient } from './rest-client';

// WebSocket message types matching legacy API
export interface StatusUpdateMessage {
  type: 'status_update';
  rider_id: string;
  action: 'checkin' | 'checkout';
  status: boolean;
  user_id: string;
  update_id?: string;
}

export interface PracticeStateMessage {
  type: 'practice_state';
  data: {
    checkin_data: Record<string, number>;
    checkout_data: Record<string, number>;
  };
}

// Connection states
export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  FAILED = 'failed',
}

// Event handlers
type StatusUpdateHandler = (message: StatusUpdateMessage) => void;
type ConnectionChangeHandler = (connected: boolean, httpFallback: boolean) => void;
type ErrorHandler = (error: string) => void;

/**
 * WebSocket Client Class
 * Matches legacy CheckinWebSocket functionality
 */
export class WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private practiceId: string | null = null;
  private userId: string | null = null;
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private httpFallbackInterval: NodeJS.Timeout | null = null;
  private lastPingTime: number | null = null;
  private httpFallbackActive: boolean = false;
  private pendingActions: any[] = [];
  private pendingUpdateTimeouts: Map<string, NodeJS.Timeout> = new Map();

  // Event handlers
  private statusUpdateHandlers: StatusUpdateHandler[] = [];
  private connectionChangeHandlers: ConnectionChangeHandler[] = [];
  private errorHandlers: ErrorHandler[] = [];

  constructor() {
    this.url = import.meta.env.VITE_WEBSOCKET_URL;
    this.setupVisibilityHandler();
  }

  /**
   * Connect to WebSocket server with practice and user info
   */
  public async connect(practiceId: string, userId: string): Promise<void> {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      console.log('[WebSocket] Already connected or connecting');
      return;
    }

    this.practiceId = practiceId;
    this.userId = userId;

    // Get JWT token for authentication
    const tokens = getStoredTokens();
    if (!tokens || !tokens.idToken) {
      throw new Error('No authentication token available');
    }

    this.setConnectionState(ConnectionState.CONNECTING);

    try {
      // Connect with practice_id, user_id, and token as query parameters (matching legacy)
      const wsUrl = `${this.url}?practice_id=${practiceId}&user_id=${userId}&token=${encodeURIComponent(tokens.idToken)}`;
      
      // Only log on first connection attempt
      if (this.reconnectAttempts === 0) {
        console.log('[WebSocket] Attempting connection...');
      }
      
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (error) {
      this.setConnectionState(ConnectionState.FAILED);
      this.startHttpFallback();
      throw error;
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopHealthCheck();
    this.stopHttpFallback();
    
    // Clear all pending update timeouts
    this.pendingUpdateTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.pendingUpdateTimeouts.clear();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.setConnectionState(ConnectionState.DISCONNECTED);
    this.practiceId = null;
    this.userId = null;
  }

  /**
   * Update rider/coach status (checkin or checkout)
   */
  public updateStatus(riderId: string, action: 'checkin' | 'checkout', status: boolean, updateId?: string): void {
    // Generate update ID if not provided
    if (!updateId) {
      updateId = `${riderId}-${action}-${Date.now()}`;
    }
    
    const message = {
      action: 'checkin-action',
      rider_id: riderId,
      practice_id: this.practiceId,
      checkin_action: action,
      status: status,
      user_id: this.userId,
      update_id: updateId,
      timestamp: Date.now()
    };

    if (this.isConnected()) {
      this.send(message);
      
      // Set timeout for WebSocket response
      const timeoutId = setTimeout(() => {
        // If update hasn't been confirmed, fall back to HTTP
        console.warn('[WebSocket] Update timeout, falling back to HTTP');
        this.pendingUpdateTimeouts.delete(updateId!);
        this.sendViaHttp(message);
      }, 5000);
      
      // Track this timeout so we can cancel it if we get a response
      this.pendingUpdateTimeouts.set(updateId, timeoutId);
    } else {
      this.pendingActions.push(message);
      this.sendViaHttp(message);
    }
  }

  /**
   * Register handler for status updates
   */
  public onStatusUpdate(handler: StatusUpdateHandler): () => void {
    this.statusUpdateHandlers.push(handler);
    return () => {
      this.statusUpdateHandlers = this.statusUpdateHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Register handler for connection state changes
   */
  public onConnectionChange(handler: ConnectionChangeHandler): () => void {
    this.connectionChangeHandlers.push(handler);
    return () => {
      this.connectionChangeHandlers = this.connectionChangeHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Register handler for errors
   */
  public onError(handler: ErrorHandler): () => void {
    this.errorHandlers.push(handler);
    return () => {
      this.errorHandlers = this.errorHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  public isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Refresh practice data via HTTP
   */
  public async refreshPracticeData(): Promise<void> {
    if (!this.practiceId) return;

    try {
      const allEvents = await restClient.getEvents();
      const practiceData = allEvents.find(e => e.event_id === this.practiceId);

      if (practiceData) {
        // Notify handlers with updated practice state
        const checkinData = practiceData.event_checkin 
          ? (typeof practiceData.event_checkin === 'string' 
              ? JSON.parse(practiceData.event_checkin) 
              : practiceData.event_checkin)
          : {};
        
        const checkoutData = practiceData.event_checkout
          ? (typeof practiceData.event_checkout === 'string'
              ? JSON.parse(practiceData.event_checkout)
              : practiceData.event_checkout)
          : {};

        // Emit individual status updates for each rider
        Object.keys(checkinData).forEach(riderId => {
          this.notifyStatusUpdate({
            type: 'status_update',
            rider_id: riderId,
            action: 'checkin',
            status: checkinData[riderId] === 1,
            user_id: this.userId || '',
          });
        });

        Object.keys(checkoutData).forEach(riderId => {
          this.notifyStatusUpdate({
            type: 'status_update',
            rider_id: riderId,
            action: 'checkout',
            status: checkoutData[riderId] === 1,
            user_id: this.userId || '',
          });
        });
      }
    } catch (error) {
      console.error('[WebSocket] Failed to refresh practice data:', error);
    }
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  /**
   * Handle WebSocket open event
   */
  private handleOpen(): void {
    console.log('[WebSocket] Connected');
    this.setConnectionState(ConnectionState.CONNECTED);
    this.reconnectAttempts = 0;
    this.httpFallbackActive = false;
    this.stopHttpFallback();
    this.notifyConnectionChange(true, false);
    this.joinPractice();
    this.startHealthCheck();
    this.processPendingActions();
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data);
      console.log('[WebSocket] Message received:', message);

      // Handle pong responses
      if (message.type === 'pong') {
        this.lastPingTime = Date.now();
        return;
      }

      // Handle API Gateway error responses
      if (message.statusCode && message.statusCode >= 400) {
        this.notifyError(`API Gateway Error: ${message.message || 'Unknown error'}`);
        return;
      }

      switch (message.type) {
        case 'status_update':
          this.notifyStatusUpdate(message);
          break;
        case 'practice_state':
          this.handlePracticeState(message.data);
          break;
        case 'user_joined':
          console.log('[WebSocket] User joined:', message.user_id);
          break;
        case 'error':
          this.notifyError(message.message);
          break;
        default:
          console.log('[WebSocket] Unknown message type:', message.type, message);
      }
    } catch (error) {
      console.error('[WebSocket] Error parsing message:', error);
    }
  }

  /**
   * Handle WebSocket error
   */
  private handleError(_event: Event): void {
    // Only log on first error, not on reconnection attempts
    if (this.reconnectAttempts === 0) {
      console.warn('[WebSocket] Connection failed, using HTTP fallback');
    }
    this.setConnectionState(ConnectionState.FAILED);
    this.notifyConnectionChange(false, false);
    this.startHttpFallback();
  }

  /**
   * Handle WebSocket close event
   */
  private handleClose(_event: CloseEvent): void {
    // Only log details on first close, not on reconnection attempts
    if (this.reconnectAttempts === 0) {
      console.log('[WebSocket] Connection closed, switching to HTTP fallback');
    }
    this.setConnectionState(ConnectionState.DISCONNECTED);
    this.stopHealthCheck();
    this.notifyConnectionChange(false, false);
    this.startHttpFallback();
    this.reconnect();
  }

  /**
   * Join practice room
   */
  private joinPractice(): void {
    this.send({
      action: 'join-practice',
      practice_id: this.practiceId,
      user_id: this.userId
    });
  }

  /**
   * Send message to WebSocket server
   */
  private send(message: any): boolean {
    if (this.isConnected() && this.ws) {
      console.log('[WebSocket] Sending message:', message);
      this.ws.send(JSON.stringify(message));
      return true;
    } else {
      console.warn('[WebSocket] Not connected, cannot send message:', message);
      return false;
    }
  }

  /**
   * Handle practice state message
   */
  private handlePracticeState(data: { checkin_data: Record<string, number>; checkout_data: Record<string, number> }): void {
    console.log('[WebSocket] Practice state:', data);
    
    // Emit status updates for each rider
    if (data.checkin_data) {
      Object.keys(data.checkin_data).forEach(riderId => {
        this.notifyStatusUpdate({
          type: 'status_update',
          rider_id: riderId,
          action: 'checkin',
          status: data.checkin_data[riderId] === 1,
          user_id: this.userId || '',
        });
      });
    }

    if (data.checkout_data) {
      Object.keys(data.checkout_data).forEach(riderId => {
        this.notifyStatusUpdate({
          type: 'status_update',
          rider_id: riderId,
          action: 'checkout',
          status: data.checkout_data[riderId] === 1,
          user_id: this.userId || '',
        });
      });
    }
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  private reconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);

      // Only log on first few attempts
      if (this.reconnectAttempts <= 2) {
        console.log(`[WebSocket] Reconnecting in ${delay}ms... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      }

      this.reconnectTimer = setTimeout(() => {
        if (!this.isConnected() && this.practiceId && this.userId) {
          this.connect(this.practiceId, this.userId);
        }
      }, delay);
    } else {
      console.log('[WebSocket] Using HTTP fallback mode');
      this.startHttpFallback();
    }
  }

  /**
   * Start health check with ping/pong
   */
  private startHealthCheck(): void {
    this.stopHealthCheck();
    
    // Ping every 15 seconds
    this.pingInterval = setInterval(() => {
      if (this.isConnected()) {
        this.lastPingTime = Date.now();
        this.send({ action: 'ping', timestamp: this.lastPingTime });

        // Check for pong timeout
        setTimeout(() => {
          if (this.lastPingTime && (Date.now() - this.lastPingTime) > 8000) {
            console.warn('[WebSocket] Ping timeout, reconnecting...');
            this.ws?.close();
          }
        }, 5000);
      }
    }, 15000);

    // Check health every 30 seconds
    this.healthCheckInterval = setInterval(() => {
      if (this.lastPingTime && (Date.now() - this.lastPingTime) > 45000) {
        console.warn('[WebSocket] No recent pong, connection may be stale');
        this.ws?.close();
      }
    }, 30000);
  }

  /**
   * Stop health check
   */
  private stopHealthCheck(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  /**
   * Start HTTP fallback polling
   */
  private startHttpFallback(): void {
    if (this.httpFallbackActive) return;

    console.log('[WebSocket] HTTP fallback mode active');
    this.httpFallbackActive = true;
    this.notifyConnectionChange(false, true);

    // Poll every 5 seconds
    this.httpFallbackInterval = setInterval(() => {
      this.refreshPracticeData();
    }, 5000);
  }

  /**
   * Stop HTTP fallback polling
   */
  private stopHttpFallback(): void {
    if (this.httpFallbackInterval) {
      clearInterval(this.httpFallbackInterval);
      this.httpFallbackInterval = null;
    }
    this.httpFallbackActive = false;
  }

  /**
   * Send action via HTTP when WebSocket is unavailable
   */
  private async sendViaHttp(message: any): Promise<void> {
    try {
      if (!this.practiceId) return;

      const field = message.checkin_action === 'checkin' ? 'event_checkin' : 'event_checkout';

      // Get fresh event data
      const allEvents = await restClient.getEvents();
      const event = allEvents.find(e => e.event_id === this.practiceId);
      if (!event) throw new Error('Event not found');

      let currentData: Record<string, number> = {};
      try {
        const fieldValue = event[field as keyof typeof event];
        currentData = fieldValue ? (typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue) : {};
      } catch (e) {
        currentData = {};
      }

      if (message.status) {
        currentData[message.rider_id] = 1;
      } else {
        delete currentData[message.rider_id];
      }

      await restClient.updateEvent(this.practiceId, {
        event_id: this.practiceId,
        [field]: JSON.stringify(currentData)
      });

      console.log('[WebSocket] HTTP fallback successful for:', message);
      
      // Notify success
      this.notifyStatusUpdate({
        type: 'status_update',
        rider_id: message.rider_id,
        action: message.checkin_action,
        status: message.status,
        user_id: this.userId || '',
        update_id: message.update_id
      });
    } catch (error) {
      console.error('[WebSocket] HTTP fallback failed:', error);
      this.notifyError(error instanceof Error ? error.message : 'HTTP fallback failed');
    }
  }

  /**
   * Process pending actions when connection is restored
   */
  private processPendingActions(): void {
    if (this.pendingActions.length > 0) {
      console.log(`[WebSocket] Processing ${this.pendingActions.length} pending actions`);
      this.pendingActions.forEach(action => this.send(action));
      this.pendingActions = [];
    }
  }

  /**
   * Setup visibility change handler
   */
  private setupVisibilityHandler(): void {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        console.log('[WebSocket] Page became visible, checking connection');
        if (!this.isConnected() && this.practiceId && this.userId) {
          this.reconnectAttempts = 0;
          this.connect(this.practiceId, this.userId);
        }
        this.refreshPracticeData();
      }
    });
  }

  /**
   * Set connection state
   */
  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
  }

  /**
   * Notify status update handlers
   */
  private notifyStatusUpdate(message: StatusUpdateMessage): void {
    // Cancel pending timeout if this is a response to our update
    if (message.update_id) {
      const timeoutId = this.pendingUpdateTimeouts.get(message.update_id);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.pendingUpdateTimeouts.delete(message.update_id);
        console.log('[WebSocket] Update confirmed via WebSocket:', message.update_id);
      }
    }
    
    this.statusUpdateHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('[WebSocket] Error in status update handler:', error);
      }
    });
  }

  /**
   * Notify connection change handlers
   */
  private notifyConnectionChange(connected: boolean, httpFallback: boolean): void {
    this.connectionChangeHandlers.forEach(handler => {
      try {
        handler(connected, httpFallback);
      } catch (error) {
        console.error('[WebSocket] Error in connection change handler:', error);
      }
    });
  }

  /**
   * Notify error handlers
   */
  private notifyError(error: string): void {
    this.errorHandlers.forEach(handler => {
      try {
        handler(error);
      } catch (err) {
        console.error('[WebSocket] Error in error handler:', err);
      }
    });
  }
}

// Export singleton instance
export const websocketClient = new WebSocketClient();

export default websocketClient;
